import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { filesApi, fileUrl } from '@/utils/api';
import { toast } from 'sonner';
import { relTime } from '@/utils/time';
import {
  Search, FileText, Image as ImageIcon, Mic, Download, Trash2, Loader2,
  FolderOpen, ArrowUpRight,
} from 'lucide-react';

const FILTERS = [
  { v: 'all', label: 'All' },
  { v: 'image', label: 'Photos' },
  { v: 'file', label: 'Documents' },
  { v: 'audio', label: 'Voice notes' },
];

function fmtSize(b) {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
}

function KindIcon({ kind }) {
  if (kind === 'image') return <ImageIcon size={18} />;
  if (kind === 'audio') return <Mic size={18} />;
  return <FileText size={18} />;
}

export default function FilesPage() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await filesApi.list({ kind: kind === 'all' ? undefined : kind, search: search || undefined });
      setFiles(res.data.files || []);
    } catch { toast.error('Failed to load files'); }
    finally { setLoading(false); }
  }, [kind, search]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const remove = async (id) => {
    try { await filesApi.delete(id); setFiles(p => p.filter(f => f.id !== id)); toast.success('File removed'); }
    catch { toast.error('Failed to remove'); }
  };

  return (
    <div className="pagewrap" data-testid="files-page">
      <div className="page-head">
        <div>
          <div className="pt">Files</div>
          <div className="ps">Every photo, document and voice note from your tasks — in one place.</div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search files…"
            data-testid="files-search-input"
            className="w-full h-10 pl-9 pr-3 rounded-xl border border-border bg-white text-sm outline-none focus:border-[#6994c6]" />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {FILTERS.map(f => (
            <button key={f.v} onClick={() => setKind(f.v)} data-testid={`files-filter-${f.v}`}
              className="px-3 h-10 rounded-xl text-sm font-medium border transition-colors"
              style={kind === f.v
                ? { background: '#6994c6', color: '#fff', borderColor: '#6994c6' }
                : { background: '#fff', color: '#5a6677', borderColor: 'var(--line)' }}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-muted-foreground" /></div>
      ) : files.length === 0 ? (
        <div className="text-center py-16">
          <FolderOpen size={30} className="mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No files yet. Attach photos, documents or voice notes to your tasks and they'll show up here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="files-grid">
          {files.map(f => (
            <div key={f.id} className="rounded-2xl border border-border bg-white overflow-hidden group flex flex-col" data-testid="file-card">
              {f.kind === 'image' ? (
                <a href={fileUrl(f.id)} target="_blank" rel="noreferrer" className="block h-36 bg-secondary/40 overflow-hidden">
                  <img src={fileUrl(f.id)} alt={f.original_filename} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                </a>
              ) : (
                <div className="h-36 flex items-center justify-center" style={{ background: 'rgba(105,148,198,0.06)', color: '#6994c6' }}>
                  <KindIcon kind={f.kind} />
                </div>
              )}
              <div className="p-3 flex flex-col gap-1.5 flex-1">
                <div className="flex items-start gap-2">
                  <span style={{ color: '#6994c6' }} className="mt-0.5 shrink-0"><KindIcon kind={f.kind} /></span>
                  <span className="text-sm font-medium leading-snug break-words line-clamp-2">{f.original_filename}</span>
                </div>
                <div className="text-xs text-muted-foreground">{fmtSize(f.size)} · {relTime(f.created_at)}</div>
                {f.card_title && (
                  <button onClick={() => navigate(`/action-cards/${f.action_card_id}`)} data-testid="file-card-source-link"
                    className="text-xs font-medium inline-flex items-center gap-1 mt-0.5 self-start" style={{ color: '#6994c6' }}>
                    {f.card_title}<ArrowUpRight size={11} />
                  </button>
                )}
                <div className="flex items-center gap-1 mt-auto pt-2">
                  <a href={fileUrl(f.id)} target="_blank" rel="noreferrer" data-testid="file-download-button"
                    className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-lg text-xs font-medium border border-border hover:bg-secondary transition-colors">
                    <Download size={13} />Open
                  </a>
                  <button onClick={() => remove(f.id)} data-testid="file-delete-button"
                    className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-border hover:bg-secondary transition-colors" style={{ color: '#9aa3b0' }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
